// The analysis screen's points of view ("lenses"): the same screen, models and simulation seen through actions, people
// and roles, systems and infrastructure, and (to come) information. Each lens has its own address, so the organisation menu can go
// straight to it, and a switcher moves between them
import { AI_SUMMARY_URL, ANALYSIS_URL, INFORMATION_ANALYSIS_URL, INSIGHTS_ANALYSIS_URL, NODES_URL, OVERVIEW_URL, PEOPLE_ANALYSIS_URL, SYSTEMS_ANALYSIS_URL, TAGS_ANALYSIS_URL } from '../app-urls';
import { CURRENT_STATE_PROJECT_KEY } from '../common/linqset-model';
import { getUnsortedModelList, Organisation } from '../common/organisation';

export type LensKey = 'overview' | 'actions' | 'people' | 'systems' | 'information' | 'insights' | 'tags' | 'nodes' | 'ai';

export interface Lens {
    key: LensKey;
    label: string;
    icon: string;
    description: string;
    url: (organisationKey: string) => string;
}

export const LENSES: Lens[] = [
    { key: 'overview', label: 'Overview', icon: 'dashboard', description: 'The headline figures and what to do first', url: OVERVIEW_URL },
    { key: 'actions', label: 'Actions', icon: 'account_tree', description: 'How work flows from step to step', url: ANALYSIS_URL },
    { key: 'people', label: 'People', icon: 'groups', description: 'People and roles: how roles hand work to each other', url: PEOPLE_ANALYSIS_URL },
    { key: 'systems', label: 'Systems', icon: 'dns', description: 'Systems and infrastructure: how information moves between systems, automatically or by hand', url: SYSTEMS_ANALYSIS_URL },
    { key: 'information', label: 'Information', icon: 'inventory_2', description: 'Information assets and the outputs that are their states: the value each carries, derived from how it is used, where that value lands, and the friction behind outputs nothing uses', url: INFORMATION_ANALYSIS_URL },
    { key: 'insights', label: 'Insights', icon: 'insights', description: 'Figures, top fives and charts for each part of what is looked at', url: INSIGHTS_ANALYSIS_URL },
    { key: 'tags', label: 'Tags', icon: 'sell', description: 'How the work splits between the tags of each tag group, and which actions carry each tag', url: TAGS_ANALYSIS_URL },
    { key: 'nodes', label: 'Nodes', icon: 'table_chart', description: 'Every node by type, with its figures', url: NODES_URL },
    { key: 'ai', label: 'AI summary', icon: 'auto_awesome', description: 'A written summary of what is looked at, by AI', url: AI_SUMMARY_URL },
];

// each model's colour, the same in every lens
export const MODEL_COLOURS = [
    'Crimson', 'DarkOrange', 'Gold', 'LawnGreen', 'ForestGreen', 'MediumSpringGreen', 'LightSeaGreen', 'DeepSkyBlue',
    'RoyalBlue', 'MidnightBlue', 'Indigo', 'DarkOrchid', 'Magenta', 'DeepPink', 'SaddleBrown', 'Chocolate', 'BurlyWood',
    'SlateGray', 'DarkSlateGray', 'DarkKhaki',
];

// a colour for each model, by its place among all the organisation's current state models, so a model keeps its colour
// whichever models are shown and whichever lens shows them
export const modelColours = (organisation: Organisation): Map<string, string> => {
    const models = getUnsortedModelList(organisation).filter(m => m.projectKey === CURRENT_STATE_PROJECT_KEY);
    return new Map(models.map((m, index) => [m._key, MODEL_COLOURS[index % MODEL_COLOURS.length]]));
};

